import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Lightbulb,
  RefreshCw,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Clock
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useCollegeContext } from '../../../contexts/CollegeContext';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { supabase } from '../../../lib/supabase';

// Import view components
import EssayIdeasHub from '../EssayIdeas/EssayIdeasHub';
import EssaySlotsLibrary from '../EssayIdeas/EssaySlotsLibrary';
import FreewriteInterface from '../FreewriteInterface';
import OutlineLibrary from '../Outlines/OutlineLibrary';
import OutlineEditor from '../Outlines/OutlineEditor';
import OutlineDraftDeveloper from '../Outlines/OutlineDraftDeveloper';
import EssayDevelopmentInterface from '../EssayDevelopmentInterface';
import ConversationalEssayBuilder from '../ConversationalEssayBuilder';
import EssaySlotDashboard from '../Essays/EssaySlotDashboard';
import DraftLibraryWithFindIdeas from '../Essays/DraftLibraryWithFindIdeas';

// Essay Hub modes - matching the mockup tabs
const ESSAY_MODES = {
  FREE_WRITING: 'freewriting',
  ESSAY_IDEATION: 'ideation',
  TOPICS_IDEAS: 'topics',
  OUTLINES: 'outlines',
  ESSAY_WORKSPACE: 'workspace',
  ESSAY_SLOTS: 'slots'
};

const UnifiedEssayHub = ({ showHeader = true }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useFamilyContext();
  const { 
    essays, 
    colleges, 
    assessmentData,
    personalProfile,
    addEssay,
    updateEssay,
    permanentDeleteEssay
  } = useCollegeContext();

  // Get mode from URL params, default to Essay Ideation
  const mode = searchParams.get('mode') || ESSAY_MODES.ESSAY_IDEATION;
  const selectedSlotId = searchParams.get('slot'); // Get slot ID from URL

  // Local state
  const [selectedEssay, setSelectedEssay] = useState(null);
  const [selectedFreewrite, setSelectedFreewrite] = useState(null);
  const [isLoading] = useState(false);
  const [freewrites, setFreewrites] = useState([]);
  const [editingOutline, setEditingOutline] = useState(null);
  const [developingOutline, setDevelopingOutline] = useState(null);
  const [libraryRefreshKey] = useState(0); // Will be used for refreshing library when interviews complete
  const [selectedSlot, setSelectedSlot] = useState(null); // Track selected slot

  // Recent Activity dropdown state
  const [isRecentActivityOpen, setIsRecentActivityOpen] = useState(false);
  const recentActivityRef = useRef(null);
  
  // Flow state management
  const [isInFlowState, setIsInFlowState] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recentActivityRef.current && !recentActivityRef.current.contains(event.target)) {
        setIsRecentActivityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-select slot when slot ID is in URL
  useEffect(() => {
    if (selectedSlotId && currentUser?.id) {
      // Fetch the slot details
      const loadSelectedSlot = async () => {
        try {
          const { data: slot, error } = await supabase
            .from('essay_slots')
            .select('*, college_journey_colleges!inner(name)')
            .eq('id', selectedSlotId)
            .eq('user_id', currentUser.id)
            .single();

          if (error) throw error;

          if (slot) {
            setSelectedSlot(slot);
            console.log('Auto-selected slot from URL:', slot);

            // If the slot has an assigned essay, load and select it
            if (slot.assigned_essay_id) {
              const essay = essays?.find(e => e.id === slot.assigned_essay_id);
              if (essay) {
                setSelectedEssay(essay);
                // Switch to workspace mode to edit the essay
                const params = new URLSearchParams(searchParams);
                params.set('mode', ESSAY_MODES.ESSAY_WORKSPACE);
                setSearchParams(params);
              }
            }
          }
        } catch (error) {
          console.error('Error loading selected slot:', error);
          toast.error('Could not load essay slot');
        }
      };

      loadSelectedSlot();
    }
  }, [selectedSlotId, currentUser?.id, essays, searchParams, setSearchParams]);

  // Load freewrites from database
  const loadFreewrites = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('college_journey_freewrites')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('date', { ascending: false })
        .limit(20);

      if (error) throw error;
      setFreewrites(data || []);
    } catch (error) {
      console.error('Error loading freewrites:', error);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadFreewrites();
  }, [loadFreewrites]);

  // Calculate metrics for Journey Status Bar
  const metrics = useMemo(() => {
    const activeEssays = essays?.filter(e => !e.deleted) || [];

    // Count freewrites from dedicated freewrites table
    const freeWritesCount = freewrites.length;
    
    // Count saved ideas (from essay_ideas table or essays with 'idea' type)
    const savedIdeasCount = activeEssays.filter(e => 
      e.essay_type === 'idea' || e.is_idea
    ).length;
    
    // Essays with actual content started
    const essaysStarted = activeEssays.filter(e => 
      e.content && e.content.length > 50 && !e.is_freewrite && !e.is_idea
    ).length;
    
    // Total essay slots needed (typically based on colleges)
    const essaysTotal = colleges?.length > 0 ? colleges.length + 5 : 5; // 1 per college + common app etc
    
    // Essay slots (college-specific essays)
    const essaySlots = colleges?.length || 0;
    const essaySlotsStarted = activeEssays.filter(e => e.college_id).length;
    
    return {
      freeWritesCount,
      savedIdeasCount,
      essaysStarted,
      essaysTotal,
      essaySlots,
      essaySlotsStarted,
      assessmentComplete: !!assessmentData,
      hasPersonalizedTopics: !!assessmentData
    };
  }, [essays, colleges, assessmentData, freewrites]);

  // Get recent activity across all essay work
  const recentActivity = useMemo(() => {
    const activeEssays = essays?.filter(e => !e.deleted) || [];

    // Combine essays and freewrites for recent activity
    const combinedActivity = [
      // Map essays
      ...activeEssays.map(essay => ({
        id: essay.id,
        type: essay.is_idea || essay.essay_type === 'idea' ? 'idea' : 'draft',
        title: essay.title || 'Untitled Essay',
        updatedAt: new Date(essay.updated_at || essay.last_updated),
        isEssay: true
      })),
      // Map freewrites
      ...freewrites.map(freewrite => ({
        id: freewrite.id,
        type: 'freewrite',
        title: `Freewrite - ${new Date(freewrite.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        updatedAt: new Date(freewrite.last_updated || freewrite.created_at),
        isEssay: false,
        freewriteData: freewrite
      }))
    ];

    // Sort by most recent and take top 5
    return combinedActivity
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, 5)
      .map(item => {
        const now = new Date();
        const diffHours = Math.floor((now - item.updatedAt) / (1000 * 60 * 60));

        let dateStr;
        if (diffHours < 1) dateStr = 'Just now';
        else if (diffHours < 24) dateStr = `${diffHours}h ago`;
        else if (diffHours < 48) dateStr = 'Yesterday';
        else dateStr = `${Math.floor(diffHours / 24)} days ago`;

        return {
          id: item.id,
          type: item.type,
          title: item.title,
          date: dateStr,
          isEssay: item.isEssay,
          freewriteData: item.freewriteData
        };
      });
  }, [essays, freewrites]);

  // Essay management functions - using context functions for proper state management
  const handleCreateEssay = useCallback(async (essayData) => {
    if (!currentUser?.id) {
      console.error('No current user ID');
      toast.error('Please log in to create essays');
      return null;
    }

    try {
      console.log('🔍 Creating essay with data:', essayData);
      const newEssay = await addEssay(essayData);
      
      if (!newEssay) {
        console.error('❌ addEssay returned null - likely missing family context');
        toast.error('Unable to create essay. Please refresh the page and try again.');
        return null;
      }
      
      console.log('✅ Essay created successfully:', newEssay);
      toast.success('Essay created successfully!');
      return newEssay;
    } catch (error) {
      console.error('❌ Error creating essay:', error);
      toast.error('Failed to create essay: ' + (error.message || 'Unknown error'));
      throw error;
    }
  }, [currentUser, addEssay]);

  const handleUpdateEssay = useCallback(async (essayId, updates) => {
    try {
      console.log('🔍 Updating essay:', essayId, 'with updates:', updates);
      const updatedEssay = await updateEssay(essayId, updates);
      
      if (!updatedEssay) {
        console.error('❌ updateEssay returned null');
        return null;
      }
      
      console.log('✅ Essay updated successfully');
      return updatedEssay;
    } catch (error) {
      console.error('❌ Error updating essay:', error);
      // Only show error toast for non-auth errors (auth errors are handled elsewhere)
      if (!error.code || error.code !== 'AUTH_ERROR') {
        toast.error('Failed to update essay: ' + (error.message || 'Unknown error'));
      }
      throw error;
    }
  }, [updateEssay]);

  const handleDeleteEssay = useCallback(async (essayId) => {
    try {
      await permanentDeleteEssay(essayId);
      toast.success('Essay deleted successfully');
    } catch (error) {
      console.error('Error deleting essay:', error);
      toast.error('Failed to delete essay');
      throw error;
    }
  }, [permanentDeleteEssay]);

  const handleSelectEssay = useCallback((essay) => {
    setSelectedEssay(essay);
  }, []);

  // Handle mode changes
  const handleModeChange = useCallback((newMode, clearFreewrite = false) => {
    const params = new URLSearchParams();
    params.set('mode', newMode);
    setSearchParams(params);
    // Reset flow state when changing modes
    setIsInFlowState(false);
    setHasStartedTyping(false);
    // Clear selected freewrite when explicitly requested (e.g., clicking Free Writing tab)
    if (clearFreewrite) {
      setSelectedFreewrite(null);
    }
  }, [setSearchParams]);

  // Handle navigation back to College Journey
  const handleBackToCollege = useCallback(() => {
    navigate('/college');
  }, [navigate]);

  // Auto-hide sidebar in Free Writing mode when user starts typing
  useEffect(() => {
    if (mode === ESSAY_MODES.FREE_WRITING && !hasStartedTyping) {
      // Listen for any typing in textareas
      const handleKeyDown = (e) => {
        // Check if user is typing in a textarea (the main writing area)
        if (e.target.tagName === 'TEXTAREA' && e.key.length === 1) {
          setHasStartedTyping(true);
          // Enter flow state after first few keystrokes
          setTimeout(() => {
            setIsInFlowState(true);
          }, 1500); // Give them 1.5s before entering flow state
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [mode, hasStartedTyping]);

  // Toggle Recent Activity dropdown
  const toggleRecentActivity = useCallback(() => {
    setIsRecentActivityOpen(prev => !prev);
  }, []);

  // Handle activity item click
  const handleActivityClick = useCallback((activity) => {
    // Close dropdown after selection
    setIsRecentActivityOpen(false);
    
    if (activity.type === 'freewrite') {
      // Handle freewrite click - load the specific freewrite
      const freewrite = freewrites.find(f => f.id === activity.id);
      if (freewrite) {
        setSelectedFreewrite(freewrite);
        handleModeChange(ESSAY_MODES.FREE_WRITING);
      }
    } else {
      // Navigate to appropriate view based on type for essays
      const essay = essays?.find(e => e.id === activity.id);
      if (essay) {
        setSelectedEssay(essay);
        if (activity.type === 'idea') {
          handleModeChange(ESSAY_MODES.ESSAY_IDEATION);
        } else {
          handleModeChange(ESSAY_MODES.ESSAY_WORKSPACE);
        }
      }
    }
  }, [essays, freewrites, handleModeChange]);

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {/* Flow State Indicator - Subtle zen message */}
      {isInFlowState && mode === ESSAY_MODES.FREE_WRITING && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-teal-500/90 text-white text-sm font-medium rounded-full shadow-lg backdrop-blur-sm flex items-center gap-2 animate-fade-in">
          <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
          Flow mode active
        </div>
      )}
      
      {/* Header */}
      {showHeader && (
        <div className={` flex items-center justify-between p-3 bg-slate-900 border-b-2 border-slate-900 transition-opacity duration-700 ${
          isInFlowState ? 'opacity-60' : 'opacity-100'
        }`}>
         
            <div className="flex items-start justify-between mb-1">


              


            {/* Journey Status Bar - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                <span className="text-stone-300">Free Writes: <span className="text-white font-medium">{metrics.freeWritesCount}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gold-300"></div>
                <span className="text-stone-300">Ideas Saved: <span className="text-white font-medium">{metrics.savedIdeasCount}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                <span className="text-stone-300">Essays Started: <span className="text-white font-medium">{metrics.essaysStarted}/{metrics.essaysTotal}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-coral-400"></div>
                <span className="text-stone-300">Essay Slots: <span className="text-white font-medium">{metrics.essaySlotsStarted}/{metrics.essaySlots}</span></span>
              </div>
              {metrics.assessmentComplete && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-azure-500" />
                  <span className="text-stone-100 font-medium">Assessment Complete</span>
                </div>
              )}
            </div>
          
        </div>
      )
                    {/* Header Actions */}
                {/* Recent Activity Dropdown */}
                <div className="relative" ref={recentActivityRef}>
                  <button 
                    onClick={toggleRecentActivity}
                    className="px-4 py-2 bg-white text-slate-900 border border-slate-900 rounded-lg font-medium text-xs hover:bg-stone-50 transition-all active:scale-95 flex items-center gap-2"
                  >
                    <Clock className="w-3.5 h-3.5" />
                    Recent Activity
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isRecentActivityOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isRecentActivityOpen && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-slate-900 rounded-lg shadow-xl overflow-hidden z-50">
                      <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                          <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Recent Activity</h3>
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {recentActivity.length > 0 ? (
                          <div className="p-3 space-y-2">
                            {recentActivity.map((item) => (
                              <button 
                                key={item.id} 
                                onClick={() => handleActivityClick(item)}
                                className="w-full text-left p-3 border border-slate-300 rounded-lg hover:border-slate-900 hover:bg-stone-50 transition-all group"
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                                    item.type === 'freewrite' ? 'bg-magenta-500' :
                                    item.type === 'draft' ? 'bg-azure-500' :
                                    'bg-teal-500'
                                  }`}></div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                                    <p className="text-xs text-stone-600">{item.date}</p>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0" />
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 px-4 text-sm text-stone-500">
                            No recent activity yet. Start writing!
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
               
              </div>
          )}

      {/* Contextual Status Banner */}
      {metrics.assessmentComplete && metrics.hasPersonalizedTopics && (
        <div className="bg-azure-50 border-b-2 border-azure-600">
          <div className="max-w-[1800px] mx-auto px-8 py-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-azure-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">
                  You've completed your strategic assessment - <span className="font-bold">personalized topics</span> ready to explore
                </p>
                <p className="text-xs text-azure-800 mt-1">Your Strategic Assessment Topics are curated based on your unique experiences and responses</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Option Selector */}
      <div className={`bg-stone-50 transition-opacity duration-700 ${
        isInFlowState ? 'opacity-40' : 'opacity-100'
      }`}>
        <div className="max-w-[1800px] mx-auto px-4 md:px-8 py-4 md:py-6">

        </div>
      </div>

      {/* Module View - Full Width */}
      <div className="max-w-[1800px] mx-auto px-8 pt-4 pb-12">
        <div className="relative">
          {/* Main Content Area */}
          <div className="w-full relative">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : (
              <>
                {mode === ESSAY_MODES.FREE_WRITING && (
                  <FreewriteInterface
                    userId={currentUser?.id}
                    selectedDate={selectedFreewrite?.date}
                    existingContent={selectedFreewrite?.content}
                    existingPrompt={selectedFreewrite?.prompt}
                    lastModified={selectedFreewrite?.last_updated}
                    onSave={() => {
                      // Refresh freewrites to update Recent Activity
                      loadFreewrites();
                    }}
                    onSessionComplete={(stats) => {
                      // When session completes, exit flow state
                      setIsInFlowState(false);
                      // Refresh the freewrites list
                      loadFreewrites();
                      // Clear selected freewrite
                      setSelectedFreewrite(null);
                      toast.success(`Freewriting session complete! ${stats.words} words captured.`);
                    }}
                    onToggleSidebar={() => {
                      // When DONE is clicked, refresh and clear
                      loadFreewrites();
                      // Clear selected freewrite so next time they start fresh
                      setSelectedFreewrite(null);
                    }}
                  />
                )}
                
                {mode === ESSAY_MODES.ESSAY_IDEATION && (
                  <EssayIdeasHub
                    assessmentData={assessmentData}
                    personalProfile={personalProfile}
                    colleges={colleges}
                    hideLibrary={true}
                    onCreateEssay={() => {
                      // Handle creating new essay from idea
                      toast.success('Essay created from idea!');
                      handleModeChange(ESSAY_MODES.ESSAY_WORKSPACE);
                    }}
                    onNavigateToOutlines={() => {
                      // Navigate to Outlines tab after saving outline
                      handleModeChange(ESSAY_MODES.OUTLINES);
                    }}
                  />
                )}

                {mode === ESSAY_MODES.TOPICS_IDEAS && (
                  <EssaySlotsLibrary
                    key={libraryRefreshKey}
                    refreshKey={libraryRefreshKey}
                    onNavigateToOutlines={() => {
                      handleModeChange(ESSAY_MODES.OUTLINES);
                    }}
                  />
                )}

                {mode === ESSAY_MODES.OUTLINES && (
                  <OutlineLibrary
                    onCreateNew={() => {
                      // TODO: Open outline creator modal
                      console.log('Create new outline clicked');
                      toast('Outline creator coming soon!');
                    }}
                    onEditOutline={(outline) => {
                      console.log('Opening outline editor for:', outline.id);
                      setEditingOutline(outline);
                    }}
                    onCopyToDraft={async (outline) => {
                      try {
                        console.log('📄 Copying outline to draft:', outline.id);

                        // Convert outline sections to essay content
                        const outlineData = outline.outline_data || {};
                        const sections = [
                          { key: 'hook', label: 'Opening Hook' },
                          { key: 'context', label: 'Context/Background' },
                          { key: 'rising_action', label: 'Rising Action' },
                          { key: 'turning_point', label: 'Turning Point' },
                          { key: 'resolution', label: 'Resolution' },
                          { key: 'closing', label: 'Closing' }
                        ];

                        // Build initial essay content from outline
                        let essayContent = '';
                        if (outline.strategic_angle) {
                          essayContent += `STRATEGIC ANGLE:\n${outline.strategic_angle}\n\n---\n\n`;
                        }

                        sections.forEach(({ key, label }) => {
                          if (outlineData[key]) {
                            essayContent += `[${label}]\n${outlineData[key]}\n\n`;
                          }
                        });

                        // Create new essay
                        const newEssay = await addEssay({
                          title: outline.title || 'Untitled Essay',
                          content: essayContent,
                          type: 'supplemental',
                          status: 'draft',
                          source: 'outline_copy',
                          notes: `Created from outline: ${outline.id}`
                        });

                        // Update outline with essay_id link
                        await supabase
                          .from('college_journey_essay_outlines')
                          .update({ essay_id: newEssay.id })
                          .eq('id', outline.id);

                        console.log('✅ Created essay from outline:', newEssay.id);
                        toast.success('Outline copied to draft!');

                        // Navigate to workspace and select the new essay
                        setSelectedEssay(newEssay);
                        handleModeChange(ESSAY_MODES.ESSAY_WORKSPACE);

                      } catch (error) {
                        console.error('❌ Error copying outline to draft:', error);
                        toast.error('Failed to copy outline');
                      }
                    }}
                    onDevelopDraft={(outline) => {
                      console.log('📝 Opening AI-guided draft development for:', outline.id);
                      setDevelopingOutline(outline);
                    }}
                  />
                )}

                {mode === ESSAY_MODES.ESSAY_WORKSPACE && !selectedEssay && (
                  <DraftLibraryWithFindIdeas
                    essays={essays || []}
                    onSelectEssay={handleSelectEssay}
                    onCreateNew={handleCreateEssay}
                    onDeleteEssay={handleDeleteEssay}
                    colleges={colleges || []}
                    onNavigateToFindIdeas={() => handleModeChange(ESSAY_MODES.ESSAY_IDEATION)}
                  />
                )}

                {mode === ESSAY_MODES.ESSAY_WORKSPACE && selectedEssay && (
                  <EssayDevelopmentInterface
                    userId={currentUser?.id}
                    essays={essays}
                    selectedEssay={selectedEssay}
                    onSelectEssay={(essay) => {
                      if (!essay) {
                        // If essay is cleared, go back to draft library
                        setSelectedEssay(null);
                      } else {
                        setSelectedEssay(essay);
                      }
                    }}
                    onCreateEssay={handleCreateEssay}
                    onUpdateEssay={handleUpdateEssay}
                    onDeleteEssay={handleDeleteEssay}
                    initialMode="writing"
                    onModeChange={(newMode) => {
                      if (newMode === 'selection') {
                        // If they navigate back to selection, clear the selected essay
                        setSelectedEssay(null);
                      }
                    }}
                  />
                )}
                
                {mode === ESSAY_MODES.ESSAY_SLOTS && (
                  <EssaySlotDashboard
                    userId={currentUser?.id}
                    initialSelectedSlotId={selectedSlotId} // Pass the slot ID from URL
                    onSlotClick={(slot) => {
                      // Handle slot click - could navigate to essay workspace
                      console.log('Slot clicked:', slot);
                      setSelectedSlot(slot);

                      if (slot.assigned_essay_id) {
                        // If there's an assigned essay, find it and switch to workspace
                        const essay = essays?.find(e => e.id === slot.assigned_essay_id);
                        if (essay) {
                          setSelectedEssay(essay);
                          handleModeChange(ESSAY_MODES.ESSAY_WORKSPACE);
                        }
                      }
                    }}
                    onAssignContent={(contentType, slotId) => {
                      console.log('Assign content:', contentType, slotId);
                      toast.success('Content assignment feature coming soon');
                    }}
                  />
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {/* Outline Editor Modal */}
      {editingOutline && (
        <OutlineEditor
          outline={editingOutline}
          onClose={() => setEditingOutline(null)}
          onSave={(updatedOutline) => {
            console.log('Outline saved:', updatedOutline);
            setEditingOutline(null);
            // Trigger refresh of OutlineLibrary by forcing a re-render
            // The library will reload via its useEffect
          }}
        />
      )}

      {/* Outline Draft Developer Modal */}
      {developingOutline && (
        <OutlineDraftDeveloper
          outline={developingOutline}
          onClose={() => setDevelopingOutline(null)}
          onSave={async (draftData) => {
            try {
              console.log('💾 Saving developed draft:', draftData);

              // Create new essay from developed draft
              const newEssay = await addEssay({
                title: developingOutline.title || 'Untitled Essay',
                content: draftData.content,
                type: 'supplemental',
                status: 'draft',
                source: 'outline_development',
                notes: `Developed from outline: ${developingOutline.id}`,
                word_count: draftData.wordCount
              });

              // Update outline with essay_id link
              await supabase
                .from('college_journey_essay_outlines')
                .update({ essay_id: newEssay.id })
                .eq('id', developingOutline.id);

              console.log('✅ Created essay from developed draft:', newEssay.id);

              // Close developer and navigate to workspace
              setDevelopingOutline(null);
              setSelectedEssay(newEssay);
              handleModeChange(ESSAY_MODES.ESSAY_WORKSPACE);

              toast.success('Draft completed and saved!');
            } catch (error) {
              console.error('❌ Error saving developed draft:', error);
              toast.error('Failed to save draft');
              throw error;
            }
          }}
        />
      )}
    </div>
  );
};

export default UnifiedEssayHub;
